import Icon from '@components/v2/ui/Icon/Icon';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { CircleAlert, CircleCheck } from 'lucide-react';
import type { ElementType } from 'react';
import LangEnum from '../../../../lang/LangEnum';
import { useTranslation } from '../../../../modules/translation/TranslationContext';

export type AlertSeverity = 'success' | 'error' | 'warning' | 'info';

interface AlertProps {
  message: LangEnum | string;
  severity?: AlertSeverity;
  values?: Record<string, unknown>;
  testId?: string;
}

const ICON_BY_SEVERITY: Record<AlertSeverity, ElementType> = {
  success: CircleCheck,
  error: CircleAlert,
  warning: CircleAlert,
  info: CircleAlert,
};

const Alert = ({ message, severity = 'info', values, testId }: AlertProps) => {
  const { translate } = useTranslation();

  return (
    <Box
      role="alert"
      test-id={testId}
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        width: '100%',
        boxSizing: 'border-box',
        padding: 1.5,
        borderRadius: `${theme.shape.borderRadiusXl}px`,
        backgroundColor: theme.palette[severity].light,
        border: `1px solid ${alpha(theme.palette[severity].main, 0.2)}`,
      })}
    >
      <Box
        component="span"
        sx={(theme) => ({ display: 'inline-flex', color: theme.palette[severity].main })}
      >
        <Icon icon={ICON_BY_SEVERITY[severity]} size="md" />
      </Box>

      <Typography variant="body2" color="text.secondary">
        {translate(message as LangEnum, values)}
      </Typography>
    </Box>
  );
};

export default Alert;
